<script>
    import { createEventDispatcher } from 'svelte';
    import { navigate } from 'svelte-routing';
    import { getStatus, isRejected, isCorrected } from '../lib/orderStatus.js';

    export let tasks = [];
    export let currentMonth = new Date();

    const dispatch = createEventDispatcher();

    const FALLBACK_EVENT_STYLE = {
        background: '#e5e7eb',
        color: '#374151',
        borderLeft: '#9ca3af',
    };

    const REJECTED_EVENT_STYLE = {
        background: '#fecaca',
        color: '#7f1d1d',
        borderLeft: '#dc2626',
    };

    const CORRECTED_EVENT_STYLE = {
        background: '#bbf7d0',
        color: '#14532d',
        borderLeft: '#16a34a',
    };

    /** Per-task colors from shared STATUSES (same as Dashboard list chips). */
    function eventStyleAttr(task) {
        if (isCorrected(task)) {
            return `background: ${CORRECTED_EVENT_STYLE.background}; color: ${CORRECTED_EVENT_STYLE.color}; border-left-color: ${CORRECTED_EVENT_STYLE.borderLeft};`;
        }
        if (isRejected(task)) {
            return `background: ${REJECTED_EVENT_STYLE.background}; color: ${REJECTED_EVENT_STYLE.color}; border-left-color: ${REJECTED_EVENT_STYLE.borderLeft};`;
        }
        const status = getStatus(task);
        const background = status?.color ?? FALLBACK_EVENT_STYLE.background;
        const color = status?.colort ?? FALLBACK_EVENT_STYLE.color;
        const borderLeft = status?.color ?? FALLBACK_EVENT_STYLE.borderLeft;
        return `background: ${background}; color: ${color}; border-left-color: ${borderLeft};`;
    }

    function prevMonth() {
        currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
        dispatch('monthChange', { month: currentMonth });
    }

    function nextMonth() {
        currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
        dispatch('monthChange', { month: currentMonth });
    }

    function isToday(d) {
        const t = new Date();
        return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
    }

    function openTask(t) {
        const id = t?.nomer ?? t?.nomDoc;
        if (id == null || id === '' || id === 'undefined') return;
        navigate(`/order/${id}/${t.vid ?? 16}`);
    }

    $: monthLabel = currentMonth.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });

    function monthRange(date) {
        const from = new Date(date.getFullYear(), date.getMonth(), 1);
        const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return { from, to };
    }

    function buildDaysInMonth(date) {
        const { from, to } = monthRange(date);
        const days = [];
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
            days.push(new Date(d));
        }
        return days;
    }

    function toDate(v) {
        return v instanceof Date ? v : new Date(v);
    }

    function buildTasksForDay(day, taskList) {
        return taskList.filter(t => {
            const td = toDate(t.data ?? t.dataDo);
            return td.getDate() === day.getDate() &&
                td.getMonth() === day.getMonth() &&
                td.getFullYear() === day.getFullYear();
        });
    }

    // Явно указываем зависимости в самом реактивном выражении
    $: days = buildDaysInMonth(currentMonth);
    $: dayGroups = days.map(day => ({ day, tasks: buildTasksForDay(day, tasks) }));
</script>

<div class="cal-nav">
    <button class="cal-nav-btn" on:click={prevMonth}>‹</button>
    <span class="cal-month-label">{monthLabel}</span>
    <button class="cal-nav-btn" on:click={nextMonth}>›</button>
</div>

<div class="cal-day-list">
    {#each dayGroups as { day, tasks: dayTasks }}
        <div class="cal-day-row {isToday(day) ? 'today' : ''}">
            <div class="cal-day-num">{day.getDate()}</div>
            <div class="cal-day-tasks">
                {#if dayTasks.length === 0}
                    <span class="cal-empty">—</span>
                {:else}
                    {#each dayTasks as t}
                        <div
                            class="cal-event"
                            style={eventStyleAttr(t)}
                            on:click={() => openTask(t)}
                        >
                            №{t.nomDoc} · {t.proekt || t.kontrName || ''}
                        </div>
                    {/each}
                {/if}
            </div>
        </div>
    {/each}
</div>

<style>
    .cal-nav { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 10px 0; }
    .cal-nav-btn { background: none; border: none; font-size: 20px; color: #0b7285; cursor: pointer; width: 32px; height: 32px; }
    .cal-month-label { font-weight: 600; font-size: 15px; text-transform: capitalize; min-width: 140px; text-align: center; }
    .cal-day-list { display: flex; flex-direction: column; }
    .cal-day-row { display: flex; border-bottom: 1px solid #eef0f3; padding: 8px 12px; gap: 10px; }
    .cal-day-row.today { background: #fff8f0; }
    .cal-day-num { width: 28px; height: 28px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 50%; font-weight: 600; font-size: 13px; color: #2d3748; }
    .today .cal-day-num { background: #FF6B35; color: #fff; }
    .cal-day-tasks { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
    .cal-empty { color: #c0c8d0; font-size: 12px; }
    .cal-event {
        padding: 5px 9px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        border-left: 3px solid transparent;
    }
</style>
